import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const InventoryItems = () => {
    const MySwal = withReactContent(Swal);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);

    const [showModal, setShowModal] = useState(false);
    const [modalData, setModalData] = useState({ name: "", category: "", description: "" });
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);

    // Variation Modal State
    const [showVarModal, setShowVarModal] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [varData, setVarData] = useState({ name: "", unit: "kg", reorderLevel: 5, costPerUnit: 0, brand: "", size: "", type: "" });

    // Search and Filter State
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedCategory, setSelectedCategory] = useState("All");

    useEffect(() => {
        fetchProducts();
        fetchCategories();
    }, []);

    const fetchProducts = async () => {
        try {
            const res = await axios.get("https://avastar-pos-backend.onrender.com/api/inventory/products");
            setProducts(res.data);
        } catch (error) {
            console.error(error);
        }
    };

    const fetchCategories = async () => {
        try {
            const res = await axios.get("https://avastar-pos-backend.onrender.com/api/categories/active");
            setCategories(res.data.filter(c => c.type !== "Menu"));
        } catch (error) {
            console.error(error);
        }
    };

    const handleSaveProduct = async () => {
        try {
            if (!modalData.name || !modalData.category) {
                return MySwal.fire("Error", "Name and category are required", "error");
            }
            if (isEditing) {
                await axios.put(`https://avastar-pos-backend.onrender.com/api/inventory/products/${editingId}`, modalData);
                MySwal.fire("Success", "Product updated", "success");
            } else {
                await axios.post("https://avastar-pos-backend.onrender.com/api/inventory/products", modalData);
                MySwal.fire("Success", "Product created", "success");
            }
            setShowModal(false);
            fetchProducts();
        } catch (error) {
            MySwal.fire("Error", "Action failed", "error");
        }
    };

    const deleteProduct = async (id) => {
        const confirm = await MySwal.fire({
            title: "Are you sure?",
            text: "This will delete the product and all variants.",
            showCancelButton: true
        });
        if (confirm.isConfirmed) {
            await axios.delete(`https://avastar-pos-backend.onrender.com/api/inventory/products/${id}`);
            fetchProducts();
            MySwal.fire("Deleted", "Product deleted", "success");
        }
    };

    const handleSaveVariant = async () => {
        try {
            if (!varData.name || !varData.unit) {
                return MySwal.fire("Error", "Name and Unit required", "error");
            }
            const payload = {
                product: selectedProduct._id,
                name: varData.name,
                unit: varData.unit,
                reorderLevel: varData.reorderLevel,
                costPerUnit: varData.costPerUnit,
                attributes: {
                    Brand: varData.brand,
                    Size: varData.size,
                    Type: varData.type
                }
            };
            await axios.post("https://avastar-pos-backend.onrender.com/api/inventory/variants", payload);
            MySwal.fire("Success", "Variant added", "success");
            setShowVarModal(false);
            fetchProducts();
        } catch (error) {
            MySwal.fire("Error", "Failed to add variant", "error");
        }
    };

    const deleteVariant = async (id) => {
        const confirm = await await MySwal.fire({ title: "Delete Variant?", showCancelButton: true });
        if (confirm.isConfirmed) {
            await axios.delete(`https://avastar-pos-backend.onrender.com/api/inventory/variants/${id}`);
            fetchProducts();
        }
    };



    // Filter Logic
    const filteredProducts = products.filter(product => {
        const matchesCategory = selectedCategory === "All" || (product.category?._id === selectedCategory);
        const searchLower = searchQuery.toLowerCase();
        
        const matchesName = product.name?.toLowerCase().includes(searchLower);
        const matchesCategoryName = product.category?.name?.toLowerCase().includes(searchLower);
        const matchesVariant = product.variants?.some(v => 
            v.name?.toLowerCase().includes(searchLower) ||
            Object.values(v.attributes).some(attr => attr?.toLowerCase().includes(searchLower))
        );

        return matchesCategory && (matchesName || matchesCategoryName || matchesVariant);
    });

    return (
        <div className="page-wrapper">
            <div className="content">
                <div className="page-header d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 gap-3">
                    <div>
                        <h4 className="fw-bold mb-1">Inventory Products</h4>
                        <p className="text-muted mb-0">Manage parent products and their variations</p>
                    </div>
                    <button className="btn btn-primary w-100 w-md-auto" onClick={() => { setIsEditing(false); setModalData({ name: "", category: "", description: "" }); setShowModal(true); }}>
                        <i className="fas fa-plus me-2"></i>New Parent Product
                    </button>
                </div>

                {/* Filters */}
                <div className="card shadow-sm border-0 mb-4">
                    <div className="card-body py-3 d-flex flex-column flex-md-row flex-wrap gap-3 align-items-stretch align-items-md-center">
                        <div className="input-group" style={{ flex: '1 1 300px' }}>
                            <span className="input-group-text bg-white border-end-0"><i className="fas fa-search text-muted"></i></span>
                            <input 
                                type="text" 
                                className="form-control border-start-0 ps-0" 
                                placeholder="Search by product, variant, or attribute..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <div style={{ flex: '1 1 200px' }}>
                            <select 
                                className="form-select" 
                                value={selectedCategory} 
                                onChange={(e) => setSelectedCategory(e.target.value)}
                            >
                                <option value="All">All Categories</option>
                                {categories.map(c => (
                                    <option key={c._id} value={c._id}>{c.type} - {c.name}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
                
                <div className="inventory-grid">
                    {filteredProducts.map(product => (
                        <div key={product._id} className="mb-5 bg-white p-4 rounded-4 shadow-sm border border-light">
                            {/* Product Group Header */}
                            <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-4 pb-3 border-bottom">
                                <div>
                                    <h4 className="fw-bold mb-1 text-dark">{product.name}</h4>
                                    <span className="badge bg-secondary bg-opacity-10 text-secondary border">{product.category?.name} | {product.category?.type}</span>
                                </div>
                                <div className="d-flex gap-2 mt-3 mt-md-0">
                                    <button className="btn btn-sm btn-light fw-bold text-muted border shadow-sm" onClick={() => { setIsEditing(true); setEditingId(product._id); setModalData({ name: product.name, category: product.category?._id, description: product.description }); setShowModal(true); }}>
                                        <i className="fas fa-edit me-1"></i> Edit Group
                                    </button>
                                    <button className="btn btn-sm btn-light fw-bold text-danger border shadow-sm" onClick={() => deleteProduct(product._id)}>
                                        <i className="fas fa-trash-alt me-1"></i> Delete
                                    </button>
                                    <button className="btn btn-sm btn-primary fw-bold shadow-sm" onClick={() => {
                                        setSelectedProduct(product);
                                        setVarData({ name: "", unit: "kg", reorderLevel: 5, costPerUnit: 0, brand: "", size: "", type: "" });
                                        setShowVarModal(true);
                                    }}>
                                        <i className="fas fa-plus-circle me-1"></i> Add Batch/Variant
                                    </button>
                                </div>
                            </div>

                            {/* Variants Grid */}
                            {product.variants?.length === 0 ? (
                                <div className="text-center text-muted py-5 bg-light rounded-3 border-dashed">
                                    <i className="fas fa-box-open fs-2 mb-2 text-secondary opacity-50"></i>
                                    <p className="mb-0 fw-medium">No inventory or batches added for this group yet.</p>
                                </div>
                            ) : (
                                <div className="row g-3">
                                    {product.variants.map(v => (
                                        <div className="col-12 col-md-6 col-xl-4 col-xxl-3" key={v._id}>
                                            <div className="card h-100 border-1 shadow-none variant-card" style={{ borderColor: v.currentStock <= v.reorderLevel ? '#fecaca' : '#e2e8f0', borderRadius: '16px', transition: 'all 0.2s', backgroundColor: v.currentStock <= v.reorderLevel ? '#fffcfc' : '#fff' }}>
                                                <div className="card-body p-3 d-flex flex-column">
                                                    
                                                    {/* Header */}
                                                    <div className="d-flex justify-content-between align-items-start mb-2">
                                                        <h6 className="fw-bold mb-0 text-dark lh-sm" style={{ fontSize: '15px' }}>{v.name}</h6>
                                                        <button className="btn btn-sm text-danger p-1 border-0" style={{ background: 'transparent', lineHeight: 1 }} onClick={() => deleteVariant(v._id)} title="Delete Batch">
                                                            <i className="fas fa-times"></i>
                                                        </button>
                                                    </div>
                                                    
                                                    {/* Tags */}
                                                    <div className="mb-3 d-flex flex-wrap gap-1">
                                                        {Object.entries(v.attributes).filter(([,val])=>val).map(([attrKey, val]) => (
                                                            <span key={attrKey} className="badge bg-white text-secondary border" style={{ fontSize: '10px', fontWeight: 600 }}>{val}</span>
                                                        ))}
                                                    </div>

                                                    <div className="mt-auto">
                                                        {/* Stats Grid */}
                                                        <div className="d-flex justify-content-between align-items-center bg-light p-2 rounded-3 mb-2 border">
                                                            <div>
                                                                <span className="d-block text-muted" style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>In Stock</span>
                                                                <span className={`fw-bold fs-6 ${v.currentStock <= v.reorderLevel ? 'text-danger' : 'text-success'}`}>{v.currentStock} <span style={{ fontSize: '12px' }}>{v.unit}</span></span>
                                                            </div>
                                                            <div className="text-end border-start ps-3 border-secondary border-opacity-25">
                                                                <span className="d-block text-muted" style={{ fontSize: '9px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>Cost per Unit</span>
                                                                <span className="fw-bold text-dark fs-6"><span style={{ fontSize: '11px', color: '#64748b' }}>Rs </span>{v.costPerUnit}</span>
                                                            </div>
                                                        </div>

                                                        {/* Finance Tracking */}
                                                        <div className="bg-primary bg-opacity-10 text-primary p-2 rounded-3 text-center d-flex justify-content-between align-items-center px-3 border border-primary border-opacity-25">
                                                            <span className="d-block" style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>Assets Value</span>
                                                            <span className="fw-bold fs-6">Rs {(v.currentStock * v.costPerUnit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                                        </div>
                                                    </div>

                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                    {filteredProducts.length === 0 && (
                        <div className="w-100 text-center text-muted py-5 bg-white rounded-4 shadow-sm border border-light mt-4">
                            <i className="fas fa-boxes fs-1 mb-3 text-secondary opacity-50"></i>
                            <h5 className="fw-bold">No inventory groups found.</h5>
                            <p className="mb-0">Try adjusting your search or filters, or add a new parent product.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Parent Modal */}
            {showModal && (
                <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">{isEditing ? "Edit Product" : "New Parent Product"}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">Product Name</label>
                                    <input type="text" className="form-control" value={modalData.name} onChange={e => setModalData({ ...modalData, name: e.target.value })} placeholder="e.g. Coca Cola" />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Category</label>
                                    <select className="form-control" value={modalData.category} onChange={e => setModalData({ ...modalData, category: e.target.value })}>
                                        <option value="">Select Category</option>
                                        {categories.map(c => <option key={c._id} value={c._id}>{c.type} - {c.name}</option>)}
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">Description (Optional)</label>
                                    <textarea className="form-control" value={modalData.description} onChange={e => setModalData({ ...modalData, description: e.target.value })}></textarea>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={handleSaveProduct}>Save</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Variant Modal */}
            {showVarModal && (
                <div className="modal show d-block" style={{ background: "rgba(0,0,0,0.5)", overflowY: 'auto' }}>
                    <div className="modal-dialog modal-dialog-centered">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">New Variant for {selectedProduct?.name}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowVarModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="row g-3">
                                    <div className="col-12">
                                        <label className="form-label">Variant/SKU Name <span className="text-danger">*</span></label>
                                        <input type="text" className="form-control" value={varData.name} onChange={e => setVarData({ ...varData, name: e.target.value })} placeholder="e.g. 1L Bottle" />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">Unit of Measure <span className="text-danger">*</span></label>
                                        <input type="text" className="form-control" value={varData.unit} onChange={e => setVarData({ ...varData, unit: e.target.value })} placeholder="kg, L, pcs" />
                                    </div>
                                    <div className="col-6">
                                        <label className="form-label">Alert (Low Stock) Level</label>
                                        <input type="number" className="form-control" value={varData.reorderLevel} onChange={e => setVarData({ ...varData, reorderLevel: e.target.value })} />
                                    </div>
                                    <div className="col-12">
                                        <label className="form-label">Cost per unit (LKR)</label>
                                        <input type="number" className="form-control" value={varData.costPerUnit} onChange={e => setVarData({ ...varData, costPerUnit: e.target.value })} />
                                    </div>
                                    <div className="col-12"><hr className="my-2"/></div>
                                    <div className="col-12"><label className="fw-bold mb-0">Attributes</label></div>
                                    <div className="col-4">
                                        <label className="form-label text-muted small">Brand</label>
                                        <input type="text" className="form-control form-control-sm" value={varData.brand} onChange={e => setVarData({ ...varData, brand: e.target.value })} placeholder="Coca-Cola" />
                                    </div>
                                    <div className="col-4">
                                        <label className="form-label text-muted small">Size/Vol</label>
                                        <input type="text" className="form-control form-control-sm" value={varData.size} onChange={e => setVarData({ ...varData, size: e.target.value })} placeholder="1L" />
                                    </div>
                                    <div className="col-4">
                                        <label className="form-label text-muted small">Type/Pkg</label>
                                        <input type="text" className="form-control form-control-sm" value={varData.type} onChange={e => setVarData({ ...varData, type: e.target.value })} placeholder="Bottle" />
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button className="btn btn-secondary" onClick={() => setShowVarModal(false)}>Cancel</button>
                                <button className="btn btn-primary" onClick={handleSaveVariant}>Save Variant</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};
export default InventoryItems;
